import type { LegacyRef, ReactNode } from 'react';
import type { TrackingPayload } from 'Tracking/tracking-api/useTracking';
import type { BadgeProps } from 'Components/frameworks/badge/Badge';
import type { ECBadgeProps } from 'Components/widgets/exclusive-collection-badge/ECBadge';

/** Locale row returned by SwitchLanguage.getLabelWithUrls(). */
export type SwitchLanguageLocaleInfo = {
  label: string;
  countryCode: number;
  langCode: string;
  languageNativeName: string;
  moment: string;
  locale: string;
  url: string;
};

/** Props for framework components still typed with Flow DOM `Node` (fixed in Bucket 5). */
export type ReactChildrenComponentProps = {
  children?: ReactNode;
};

/**
 * Build widget component props in spec files where only a subset of fields is
 * exercised by the test. Same contract as `makeViewProps` / `makeCmsProps`.
 */
export const makeWidgetProps = <T>(overrides: unknown): T => overrides as unknown as T;

/** Widen partial widget props at call sites (legacy extra/missing fields). */
export const asWidgetProps = <T>(props: unknown): T => props as unknown as T;

/** Formik helpers shared with Bucket 2 form widgets (re-export for Bucket 4 misc widgets). */
export { BucketFormikForm, BucketFormikInput, type BucketFormikInputProps } from 'Src/types/forms';

export {
  asGqlData,
  asLegacyDomNode,
  asLegacyHtmlCollection,
  asLegacyJsxComponent,
  asStayEntityFixture,
} from 'Src/types/view-helpers';

/** Coerce legacy ReactRef to React 18 LegacyRef at ref call sites. */
export const asLegacyRef = <T>(ref: ReactRef): LegacyRef<T> => ref as unknown as LegacyRef<T>;
export type { ThemeKey, MergedThemeStyles } from 'Src/types/theme';

/** 4th argument to `scrollToElement` (scroll-to-bottom flag). */
export const SCROLL_TO_ELEMENT_DEFAULT = false as const;

/** Cast TrackingData for useTracking() which expects a stricter payload shape. */
export const asTrackingPayload = (data: TrackingData): TrackingPayload =>
  data as unknown as TrackingPayload;

/** Badge props union may omit iconName on ECBadgeProps. */
export const badgeHasIconName = (
  badgeProps: BadgeProps | ECBadgeProps | null | undefined,
): badgeProps is BadgeProps & { iconName: string } =>
  Boolean(badgeProps && 'iconName' in badgeProps && badgeProps.iconName);
